import { useState } from 'react';
import { apiRelatorios, type ProbabilidadeDeRecuperacao } from '../lib/api.ts';
import { avisar, avisarErro } from './Avisos.tsx';

/**
 * ⭐⭐ **O ÍNDICE DE PROBABILIDADE DE RECUPERAÇÃO DO CRÉDITO — 07/10/2026.**
 *
 * Pedido da Karen, decidido por ele: três níveis, nas três pesquisas, com a
 * ampulheta da marca e a areia (a de cima é o provável; a de baixo, o remoto).
 * O desenho é o da referência que ele mandou em 08/10/2026.
 *
 * ⛔ **O Hub nunca escolhe.** Quem escolhe é o master; o texto do tópico, logo
 * abaixo, diz por quê. O desenho que aparece aqui é o MESMO que o documento
 * imprime (`/api/relatorios/:id/recuperacao.png`).
 *
 * ⚠️ **As definições são as da escala contábil de chance** — maior que a de não
 * acontecer, real mas menor, pequena —, para os três níveis quererem dizer a
 * mesma coisa em todo caso.
 */
/* ⚠️ Na mesma ordem da régua do desenho, de remota a provável. */
const NIVEIS: Array<{ chave: ProbabilidadeDeRecuperacao; nome: string; define: string }> = [
  { chave: 'remota', nome: 'Remota', define: 'A chance de recuperar o crédito é pequena.' },
  { chave: 'possivel', nome: 'Possível', define: 'Há chance real de recuperar, menor que a provável e maior que a remota.' },
  { chave: 'provavel', nome: 'Provável', define: 'A chance de recuperar o crédito é maior que a de não recuperar.' },
];

export function IndiceDeRecuperacao({
  relatorioId,
  nivel,
  entregue,
  ehMaster,
  aoMudar,
}: {
  relatorioId: string;
  nivel: ProbabilidadeDeRecuperacao | null;
  entregue: boolean;
  ehMaster: boolean;
  aoMudar: (nivel: ProbabilidadeDeRecuperacao | null) => void;
}) {
  const [gravando, setGravando] = useState(false);
  const podeMudar = ehMaster && !entregue;
  const escolhido = NIVEIS.find((n) => n.chave === nivel) ?? null;

  async function escolher(n: ProbabilidadeDeRecuperacao) {
    if (!podeMudar || gravando || n === nivel) return;
    setGravando(true);
    try {
      await apiRelatorios.definirRecuperacao(relatorioId, n);
      aoMudar(n);
      avisar(`Índice gravado: ${NIVEIS.find((x) => x.chave === n)!.nome.toLowerCase()}.`);
    } catch (e) {
      avisarErro(e);
    } finally {
      setGravando(false);
    }
  }

  return (
    <div className="indice-recuperacao">
      <span className="imv-rotulo">Probabilidade de recuperação do crédito</span>
      <div className="imv-seg" role="radiogroup" aria-label="Probabilidade de recuperação do crédito">
        {NIVEIS.map((n) => (
          <button
            key={n.chave}
            type="button"
            role="radio"
            aria-checked={nivel === n.chave}
            disabled={!podeMudar || gravando}
            className={`imv-op rec-${n.chave}${nivel === n.chave ? ' on' : ''}`}
            onClick={() => void escolher(n.chave)}
          >
            <i />
            {n.nome}
          </button>
        ))}
      </div>
      {escolhido ? (
        <>
          <p className={`imv-def rec-${escolhido.chave}`}>{escolhido.define}</p>
          <img
            className="indice-recuperacao-desenho"
            src={`/api/relatorios/${relatorioId}/recuperacao.png?n=${escolhido.chave}`}
            alt={`Índice de probabilidade de recuperação: ${escolhido.nome.toLowerCase()}`}
          />
        </>
      ) : (
        <p className="ajuda">
          {podeMudar
            ? 'Escolha o nível. É juízo da KAIRÓS, e o relatório não se entrega sem ele; o texto abaixo diz por quê.'
            : 'Ainda não escolhido. Quem escolhe é o master.'}
        </p>
      )}
    </div>
  );
}
